<script setup lang="ts" generic="TData extends object">
	import { computed, nextTick, ref, watch } from "vue";
	import CloseIcon from "./icons/CloseIcon.vue";
	import SearchIcon from "./icons/SearchIcon.vue";
	import type { FilterChip, TagDef } from "./utils/tagSearch";

	const props = defineProps<{
		tags: TagDef[];
		placeholder?: string;
		data?: TData[];
	}>();

	const chips = defineModel<FilterChip[]>("chips", { default: () => [] });
	const query = defineModel<string>("query", { default: "" });

	const inputRef = ref<HTMLInputElement | null>(null);
	const dropdownRef = ref<HTMLDivElement | null>(null);
	const inputValue = ref("");
	const isOpen = ref(false);
	const activeIndex = ref(-1);

	const isInValueMode = computed(() => inputValue.value.includes(":"));

	const tagPrefix = computed(() =>
		isInValueMode.value ? null : inputValue.value.toLowerCase().trim(),
	);

	const currentTag = computed<TagDef | undefined>(() => {
		if (!isInValueMode.value) return;

		const colonIndex = inputValue.value.indexOf(":");
		const labelPart = inputValue.value
			.slice(0, colonIndex)
			.trim()
			.toLowerCase();

		return props.tags.find(
			tag =>
				tag.label.toLowerCase() === labelPart &&
				tag.searchable !== false,
		);
	});

	const valuePrefix = computed(() => {
		if (!isInValueMode.value) return "";
		const colonIndex = inputValue.value.indexOf(":");
		return inputValue.value
			.slice(colonIndex + 1)
			.trim()
			.toLowerCase();
	});

	const filteredTags = computed(() => {
		if (isInValueMode.value) return [];

		const searchableTags = props.tags.filter(
			tag => tag.searchable !== false,
		);
		if (!tagPrefix.value) return searchableTags;

		return searchableTags.filter(tag =>
			tag.label.toLowerCase().startsWith(tagPrefix.value!),
		);
	});

	const allTagValues = computed<string[]>(() => {
		if (!isInValueMode.value || !currentTag.value) return [];
		if (currentTag.value.values) return currentTag.value.values;
		if (!props.data?.length) return [];

		const seen = new Set<string>();

		for (const row of props.data) {
			const raw = (row as Record<string, unknown>)[currentTag.value.key];
			if (raw == null || raw === "") continue;

			const values = Array.isArray(raw) ? raw : [raw];
			for (const value of values) {
				const display = currentTag.value.displayTransform
					? currentTag.value.displayTransform(value)
					: String(value).toLowerCase().trim();

				if (display) seen.add(display);
			}
		}

		return [...seen].sort();
	});

	const filteredValues = computed(() => {
		if (!isInValueMode.value || !currentTag.value) return [];

		const matchingValues = valuePrefix.value
			? allTagValues.value.filter(value =>
					value.includes(valuePrefix.value),
				)
			: allTagValues.value;

		return matchingValues.slice(0, 20);
	});

	const dropdownMode = computed<"tags" | "values">(() =>
		isInValueMode.value ? "values" : "tags",
	);

	const showDropdown = computed(
		() =>
			isOpen.value &&
			(filteredTags.value.length > 0 || filteredValues.value.length > 0),
	);

	watch(inputValue, value => {
		query.value = isInValueMode.value ? "" : value.trim();
	});

	watch([filteredTags, filteredValues], () => {
		const items =
			dropdownMode.value === "values"
				? filteredValues.value
				: filteredTags.value;
		activeIndex.value = items.length > 0 ? 0 : -1;
	});

	watch(activeIndex, () => {
		scrollActiveItemIntoView();
	});

	function openList() {
		isOpen.value = true;
		const items =
			dropdownMode.value === "values"
				? filteredValues.value
				: filteredTags.value;
		activeIndex.value = items.length > 0 ? 0 : -1;
	}

	function closeList() {
		isOpen.value = false;
		activeIndex.value = -1;
	}

	function onBlur() {
		setTimeout(closeList, 150);
	}

	function selectTag(tag: TagDef) {
		inputValue.value = `${tag.label}: `;
		nextTick(() => {
			openList();
			inputRef.value?.focus();
		});
	}

	function selectValue(value: string) {
		if (!currentTag.value) return;

		addChip({
			key: currentTag.value.key,
			label: `${currentTag.value.label}: ${value}`,
			value: value.toLowerCase(),
		});
		inputValue.value = "";
		closeList();
		nextTick(() => inputRef.value?.focus());
	}

	function addChip(chip: FilterChip) {
		if (chip.key === null) {
			const hasMatchingPlainTextChip = chips.value.some(
				existing =>
					existing.key === null && existing.value === chip.value,
			);
			if (hasMatchingPlainTextChip) return;

			chips.value.push(chip);
			return;
		}

		const tag = props.tags.find(tagDef => tagDef.key === chip.key);
		const hasMatchingTagChip = chips.value.some(
			existing =>
				existing.key === chip.key && existing.value === chip.value,
		);
		if (hasMatchingTagChip) return;

		if (tag?.allowMultiple) {
			chips.value.push(chip);
			return;
		}

		chips.value = [
			...chips.value.filter(existing => existing.key !== chip.key),
			chip,
		];
	}

	function commitInput() {
		const value = inputValue.value.trim();
		if (!value) return;

		const colonIndex = value.indexOf(":");
		if (colonIndex <= 0) {
			addChip({ key: null, label: value, value: value.toLowerCase() });
			inputValue.value = "";
			return;
		}

		const labelPart = value.slice(0, colonIndex).trim().toLowerCase();
		const valuePart = value.slice(colonIndex + 1).trim();
		const tag = props.tags.find(
			item =>
				item.label.toLowerCase() === labelPart &&
				item.searchable !== false,
		);

		if (tag && valuePart) {
			addChip({
				key: tag.key,
				label: `${tag.label}: ${valuePart}`,
				value: valuePart.toLowerCase(),
			});
			inputValue.value = "";
			return;
		}

		addChip({ key: null, label: value, value: value.toLowerCase() });
		inputValue.value = "";
	}

	function removeChip(index: number) {
		chips.value = chips.value.filter(
			(_, currentIndex) => currentIndex !== index,
		);
	}

	function onKeydown(event: KeyboardEvent) {
		if (event.key === "Enter") {
			const hasActiveSelection =
				showDropdown.value && activeIndex.value >= 0;
			const selectedTag = hasActiveSelection
				? filteredTags.value[activeIndex.value]
				: undefined;
			const selectedValue = hasActiveSelection
				? filteredValues.value[activeIndex.value]
				: undefined;

			if (dropdownMode.value === "tags" && selectedTag) {
				selectTag(selectedTag);
			} else if (dropdownMode.value === "values" && selectedValue) {
				selectValue(selectedValue);
			} else {
				commitInput();
			}

			event.preventDefault();
			return;
		}

		if (event.key === "Tab" && inputValue.value.trim()) {
			commitInput();
			event.preventDefault();
			return;
		}

		if (event.key === "Backspace" && !inputValue.value) {
			chips.value = chips.value.slice(0, -1);
			return;
		}

		if (event.key === "Escape") {
			closeList();
			if (isInValueMode.value) inputValue.value = "";
			return;
		}

		if (!showDropdown.value) return;

		const itemCount =
			dropdownMode.value === "values"
				? filteredValues.value.length
				: filteredTags.value.length;

		if (event.key === "ArrowDown") {
			activeIndex.value = Math.min(activeIndex.value + 1, itemCount - 1);
			event.preventDefault();
		} else if (event.key === "ArrowUp") {
			activeIndex.value = Math.max(activeIndex.value - 1, 0);
			event.preventDefault();
		}
	}

	function focusInput() {
		openList();
		inputRef.value?.focus();
	}

	function scrollActiveItemIntoView() {
		if (!showDropdown.value || activeIndex.value < 0) return;

		nextTick(() => {
			const activeItem = dropdownRef.value?.querySelector<HTMLElement>(
				`[data-option-index="${activeIndex.value}"]`,
			);
			activeItem?.scrollIntoView({ block: "nearest" });
		});
	}

	defineExpose({
		clear() {
			chips.value = [];
			inputValue.value = "";
			query.value = "";
		},
	});
</script>

<template>
	<div
		class="relative"
		@click="focusInput"
	>
		<div
			class="relative flex min-h-12 w-full cursor-text flex-wrap items-center gap-1 rounded-md border border-grey-150 bg-green-50 py-1 pr-8 pl-2"
		>
			<span
				v-for="(chip, i) in chips"
				:key="i"
				class="flex items-center gap-1 rounded border border-green-300 bg-green-100 px-2 py-0.5 text-sm font-bold text-grey-750"
			>
				{{ chip.label }}
				<button
					type="button"
					class="ml-0.5 text-grey-500 hover:text-red-600"
					:title="'Remove filter'"
					@click.stop="removeChip(i)"
				>
					<CloseIcon class="h-3 w-3" />
				</button>
			</span>

			<input
				ref="inputRef"
				v-model="inputValue"
				type="text"
				class="min-w-32 flex-1 bg-transparent py-0.5 font-bold text-grey-750 outline-none placeholder:font-normal placeholder:text-grey-400"
				:placeholder="
					chips.length === 0 ? (placeholder ?? 'Search') : ''
				"
				@focus="openList"
				@blur="onBlur"
				@input="openList"
				@keydown="onKeydown"
			/>

			<SearchIcon
				class="pointer-events-none absolute top-1/2 right-2 h-4 w-4 -translate-y-1/2 text-green-600"
			/>
		</div>

		<div
			v-if="showDropdown"
			ref="dropdownRef"
			class="absolute left-0 right-0 top-full z-50 mt-1 max-h-48 overflow-y-auto rounded-md border border-grey-150 bg-white shadow-lg"
		>
			<template v-if="dropdownMode === 'tags'">
					<div
						class="border-b border-grey-150 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-grey-500"
					>
						<slot name="filter-label">Filter by</slot>
					</div>
				<div
					v-for="(tag, i) in filteredTags"
					:key="tag.key"
					:data-option-index="i"
					class="flex cursor-pointer items-center gap-3 px-3 py-2 hover:bg-green-50"
					:class="{ 'bg-green-50': i === activeIndex }"
					@mousedown.prevent="selectTag(tag)"
				>
					<span class="min-w-20 text-sm font-bold text-green-700">
						{{ tag.label }}:
					</span>
					<span
						v-if="tag.description"
						class="text-sm text-grey-600"
					>
						{{ tag.description }}
					</span>
				</div>
			</template>

			<template v-else-if="filteredValues.length">
				<div
					class="border-b border-grey-150 px-3 py-1.5 text-xs font-bold uppercase tracking-wide text-grey-500"
				>
					{{ currentTag?.description ?? currentTag?.label }}
				</div>
				<div
					v-for="(value, i) in filteredValues"
					:key="value"
					:data-option-index="i"
					class="flex cursor-pointer items-center px-3 py-2 hover:bg-green-50"
					:class="{ 'bg-green-50': i === activeIndex }"
					@mousedown.prevent="selectValue(value)"
				>
					<span class="text-sm font-bold text-grey-750">{{
						value
					}}</span>
				</div>
			</template>
		</div>
	</div>
</template>
